import React, { useState, useRef, useMemo, useCallback, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email ?? '').trim());

export const emailsToTokens = (emails = []) =>
  emails.map((email) => ({ email, valid: isValidEmail(email) }));

export const tokensToValidEmails = (tokens = []) =>
  tokens.filter((t) => t.valid).map((t) => t.email);

export const tokensHaveInvalid = (tokens = []) => tokens.some((t) => !t.valid);

/** Suppress password-manager overlays (Bitwarden, 1Password, LastPass, Dashlane). */
const EXTENSION_IGNORE_INPUT_PROPS = {
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': '',
  'data-form-type': 'other',
};

const AUTOCOMPLETE_SECTION_BY_VARIANT = {
  to: 'section-town-portal-to email',
  cc: 'section-town-portal-cc email',
  bcc: 'section-town-portal-bcc email',
};

const CHIP_STYLES = {
  to: {
    valid: { bgcolor: '#e8f0fe', color: '#174ea6', deleteIcon: '#174ea6' },
    invalid: { bgcolor: '#fce8e6', color: '#c5221f', border: '1px solid #f28b82', deleteIcon: '#c5221f' },
  },
  cc: {
    valid: { bgcolor: '#e6f4ea', color: '#137333', deleteIcon: '#137333' },
    invalid: { bgcolor: '#fce8e6', color: '#c5221f', border: '1px solid #f28b82', deleteIcon: '#c5221f' },
  },
  bcc: {
    valid: { bgcolor: '#f1f3f4', color: '#5f6368', deleteIcon: '#5f6368' },
    invalid: { bgcolor: '#fce8e6', color: '#c5221f', border: '1px solid #f28b82', deleteIcon: '#c5221f' },
  },
};

const SuggestionDropdown = ({ anchorRect, suggestions, highlightIndex, onSelect }) => {
  if (!anchorRect || suggestions.length === 0) return null;

  return createPortal(
    <Paper
      elevation={8}
      role="listbox"
      sx={{
        position: 'fixed',
        top: anchorRect.bottom + 4,
        left: anchorRect.left,
        width: Math.max(anchorRect.width, 240),
        zIndex: 1600,
        maxHeight: 220,
        overflow: 'auto',
        border: '1px solid #dadce0',
        borderRadius: '4px',
      }}
    >
      {suggestions.map((email, index) => (
        <Box
          key={email}
          component="button"
          type="button"
          role="option"
          aria-selected={index === highlightIndex}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(email);
          }}
          sx={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            border: 'none',
            cursor: 'pointer',
            px: 1.5,
            py: 1,
            fontSize: 13,
            fontFamily: 'inherit',
            bgcolor: index === highlightIndex ? '#e8f0fe' : '#fff',
            color: '#202124',
            '&:hover': { bgcolor: '#e8f0fe' },
          }}
        >
          {email}
        </Box>
      ))}
    </Paper>,
    document.body
  );
};

const GmailRecipientField = ({
  label,
  tokens,
  onTokensChange,
  suggestions = [],
  placeholder = 'Add email',
  variant = 'to',
  inputId,
  loading = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  const inputRef = useRef(null);
  const ignoreBlurRef = useRef(false);
  const fallbackId = useId();
  const fieldInputId = inputId || `town-portal-${variant}-${fallbackId}`;
  const blockAutofillUntilFocus = true;
  const autoCompleteValue =
    blockAutofillUntilFocus && !inputFocused
      ? 'off'
      : AUTOCOMPLETE_SECTION_BY_VARIANT[variant] || 'email';

  const chipStyle = CHIP_STYLES[variant] || CHIP_STYLES.to;
  const showLoadingPlaceholder = loading && (variant === 'to' || variant === 'cc');

  const existingLower = useMemo(
    () => new Set(tokens.map((t) => t.email.toLowerCase())),
    [tokens]
  );

  const filteredSuggestions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return [];
    return suggestions
      .filter((s) => s && s.toLowerCase().includes(q) && !existingLower.has(s.toLowerCase()))
      .sort((a, b) => {
        const al = a.toLowerCase();
        const bl = b.toLowerCase();
        const aPrefix = al.startsWith(q);
        const bPrefix = bl.startsWith(q);
        if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
        return al.localeCompare(bl);
      })
      .slice(0, 8);
  }, [inputValue, suggestions, existingLower]);

  const updateAnchorRect = useCallback(() => {
    if (inputRef.current) {
      setAnchorRect(inputRef.current.getBoundingClientRect());
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateAnchorRect();
    const onScrollOrResize = () => updateAnchorRect();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateAnchorRect, filteredSuggestions.length]);

  const clearInput = useCallback(() => {
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const commitEmail = useCallback(
    (raw) => {
      const trimmed = String(raw ?? '').trim();
      if (!trimmed) {
        clearInput();
        setOpen(false);
        return;
      }
      const lower = trimmed.toLowerCase();
      onTokensChange((prev) => {
        if (prev.some((t) => t.email.toLowerCase() === lower)) {
          return prev;
        }
        return [...prev, { email: trimmed, valid: isValidEmail(trimmed) }];
      });
      clearInput();
      setOpen(false);
      setHighlightIndex(0);
    },
    [onTokensChange, clearInput]
  );

  const commitAutofillValue = useCallback(
    (raw) => {
      const trimmed = String(raw ?? '').trim();
      if (!trimmed || !isValidEmail(trimmed)) {
        return false;
      }
      commitEmail(trimmed);
      return true;
    },
    [commitEmail]
  );

  useEffect(() => {
    const styleId = 'town-portal-autofill-commit';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent =
        '@keyframes onAutoFillStart { from {} to {} } input:-webkit-autofill { animation: onAutoFillStart 0.001s; }';
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (showLoadingPlaceholder) return undefined;
    const el = inputRef.current;
    if (!el) return undefined;

    const isFocusedField = () => document.activeElement === el;

    const isWebkitAutofilled = () => {
      try {
        return el.matches(':-webkit-autofill');
      } catch {
        return false;
      }
    };

    const handleBrowserAutofill = () => {
      if (!isFocusedField()) return;
      commitAutofillValue(el.value);
    };

    const clearUnfocusedAutofill = () => {
      if (!isFocusedField() && el.value) clearInput();
    };

    const onNativeChange = () => {
      handleBrowserAutofill();
    };

    const onNativeInput = () => {
      window.requestAnimationFrame(() => {
        if (isWebkitAutofilled()) handleBrowserAutofill();
      });
    };

    const onAnimationStart = (e) => {
      if (e.animationName === 'onAutoFillStart') {
        window.requestAnimationFrame(() => {
          if (isFocusedField()) {
            handleBrowserAutofill();
          } else {
            clearUnfocusedAutofill();
          }
        });
      }
    };

    el.addEventListener('change', onNativeChange);
    el.addEventListener('input', onNativeInput);
    el.addEventListener('animationstart', onAnimationStart);
    return () => {
      el.removeEventListener('change', onNativeChange);
      el.removeEventListener('input', onNativeInput);
      el.removeEventListener('animationstart', onAnimationStart);
    };
  }, [commitAutofillValue, clearInput, showLoadingPlaceholder]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (/[,;]/.test(val)) {
      const parts = val.split(/[,;]/);
      parts.slice(0, -1).forEach((part) => commitEmail(part));
      const remainder = parts[parts.length - 1];
      setInputValue(remainder);
      const shouldOpen = remainder.trim().length > 0;
      setOpen(shouldOpen);
      if (shouldOpen) updateAnchorRect();
      setHighlightIndex(0);
      return;
    }
    setInputValue(val);
    const shouldOpen = val.trim().length > 0;
    setOpen(shouldOpen);
    if (shouldOpen) updateAnchorRect();
    setHighlightIndex(0);
  };

  const selectSuggestion = (email) => {
    commitEmail(email);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    const hasSuggestions = open && filteredSuggestions.length > 0;

    if (hasSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % filteredSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => (i - 1 + filteredSuggestions.length) % filteredSuggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectSuggestion(filteredSuggestions[highlightIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      if (inputValue.trim()) {
        e.preventDefault();
        const exact = filteredSuggestions.find(
          (s) => s.toLowerCase() === inputValue.trim().toLowerCase(),
        );
        commitEmail(exact || inputValue);
      }
      return;
    }

    if (e.key === 'Backspace' && !inputValue && tokens.length > 0) {
      onTokensChange((prev) => prev.slice(0, -1));
    }
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (ignoreBlurRef.current) {
        ignoreBlurRef.current = false;
        return;
      }
      const raw =
        inputValue.trim() || String(inputRef.current?.value ?? '').trim();
      if (raw) commitEmail(raw);
      clearInput();
      setOpen(false);
    }, 0);
  };

  const handleSuggestionPick = (email) => {
    ignoreBlurRef.current = true;
    selectSuggestion(email);
  };

  const removeToken = (index) =>
    onTokensChange((prev) => prev.filter((_, i) => i !== index));

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ minWidth: 48, pt: 0.75, fontSize: 13, color: '#5f6368', fontWeight: 500, flexShrink: 0 }}
      >
        {label}:
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            minHeight: 32,
            py: 0.25,
            borderBottom: '1px solid #dadce0',
            '&:focus-within': { borderBottomColor: '#1a73e8' },
          }}
          onClick={() => !showLoadingPlaceholder && inputRef.current?.focus()}
        >
          {showLoadingPlaceholder && (
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CircularProgress size={12} sx={{ color: 'inherit' }} />
                  Loading...
                </Box>
              }
              size="small"
              sx={{
                height: 24,
                fontSize: 13,
                bgcolor: chipStyle.valid.bgcolor,
                color: chipStyle.valid.color,
                opacity: 0.75,
                pointerEvents: 'none',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          {!showLoadingPlaceholder && tokens.map((token, index) => {
            const style = token.valid ? chipStyle.valid : chipStyle.invalid;
            return (
              <Chip
                key={`${token.email}-${index}`}
                label={token.email}
                size="small"
                onDelete={() => removeToken(index)}
                sx={{
                  height: 24,
                  fontSize: 13,
                  maxWidth: '100%',
                  bgcolor: style.bgcolor,
                  color: style.color,
                  border: style.border || 'none',
                  '& .MuiChip-deleteIcon': { color: style.deleteIcon, fontSize: 16 },
                }}
              />
            );
          })}
          {!showLoadingPlaceholder && (
          <input
            ref={inputRef}
            id={fieldInputId}
            name={fieldInputId}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onInput={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setInputFocused(false);
              handleBlur();
            }}
            onFocus={() => {
              setInputFocused(true);
              updateAnchorRect();
              if (inputValue.trim()) setOpen(true);
            }}
            readOnly={blockAutofillUntilFocus && !inputFocused}
            placeholder={tokens.length === 0 ? placeholder : ''}
            {...EXTENSION_IGNORE_INPUT_PROPS}
            autoComplete={autoCompleteValue}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-autocomplete="list"
            aria-controls={open ? `${fieldInputId}-listbox` : undefined}
            style={{
              border: 'none',
              outline: 'none',
              flex: 1,
              minWidth: 80,
              fontSize: 14,
              lineHeight: '24px',
              padding: '2px 4px',
              fontFamily: 'inherit',
              background: 'transparent',
            }}
          />
          )}
        </Box>

        {open && filteredSuggestions.length > 0 && (
          <SuggestionDropdown
            anchorRect={anchorRect}
            suggestions={filteredSuggestions}
            highlightIndex={highlightIndex}
            onSelect={handleSuggestionPick}
          />
        )}
      </Box>
    </Box>
  );
};

/** To row with Cc/Bcc toggles; expandable Cc and Bcc rows. */
export const TownPortalRecipientSection = ({
  toTokens,
  setToTokens,
  ccTokens,
  setCcTokens,
  bccTokens,
  setBccTokens,
  suggestions = [],
  initialShowCc = false,
  loading = false,
}) => {
  const [showCc, setShowCc] = useState(initialShowCc);
  const [showBcc, setShowBcc] = useState(false);

  useEffect(() => {
    if (initialShowCc) setShowCc(true);
  }, [initialShowCc]);

  const toggleBtnSx = (active) => ({
    textTransform: 'none',
    fontSize: 12,
    minWidth: 'auto',
    px: 1,
    py: 0.25,
    color: active ? '#1a73e8' : '#5f6368',
    fontWeight: active ? 500 : 400,
  });

  return (
    <Box component="form" data-bwignore="" onSubmit={(e) => e.preventDefault()}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GmailRecipientField
            label="To"
            variant="to"
            tokens={toTokens}
            onTokensChange={setToTokens}
            suggestions={suggestions}
            placeholder="Recipients"
            inputId="town-portal-email-to"
            loading={loading}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            flexShrink: 0,
            pt: 0.75,
            pb: 0.5,
          }}
        >
          <Button size="small" onClick={() => setShowCc((v) => !v)} sx={toggleBtnSx(showCc)}>
            Cc
          </Button>
          <Button size="small" onClick={() => setShowBcc((v) => !v)} sx={toggleBtnSx(showBcc)}>
            Bcc
          </Button>
        </Box>
      </Box>

      {(showCc || loading) && (
        <Box sx={{ pr: { xs: 0, sm: '72px' } }}>
          <GmailRecipientField
            label="Cc"
            variant="cc"
            tokens={ccTokens}
            onTokensChange={setCcTokens}
            suggestions={suggestions}
            placeholder="Cc"
            inputId="town-portal-email-cc"
            loading={loading}
          />
        </Box>
      )}

      {showBcc && (
        <Box sx={{ pr: { xs: 0, sm: '72px' } }}>
          <GmailRecipientField
            label="Bcc"
            variant="bcc"
            tokens={bccTokens}
            onTokensChange={setBccTokens}
            suggestions={suggestions}
            placeholder="Bcc"
            inputId="town-portal-email-bcc"
          />
        </Box>
      )}
    </Box>
  );
};

export default GmailRecipientField;
